import React from 'react';
type Step = {
  x: number;
  w: number;
  label: string;
  tone: 'neutral' | 'hot' | 'cool';
  faded?: boolean;
};
const UNGOVERNED: Step[] = [{
  x: 0,
  w: 56,
  label: 'draft',
  tone: 'neutral'
}, {
  x: 78,
  w: 46,
  label: 'r1',
  tone: 'hot'
}, {
  x: 140,
  w: 46,
  label: 'r2',
  tone: 'hot'
}, {
  x: 202,
  w: 46,
  label: 'r3',
  tone: 'hot'
}, {
  x: 264,
  w: 46,
  label: 'r4',
  tone: 'hot'
}, {
  x: 326,
  w: 46,
  label: 'r5',
  tone: 'hot'
}];
const SEATED: Step[] = [{
  x: 0,
  w: 56,
  label: 'draft',
  tone: 'neutral'
}, {
  x: 78,
  w: 46,
  label: 'r1',
  tone: 'cool'
}, {
  x: 140,
  w: 46,
  label: 'r2',
  tone: 'cool',
  faded: true
}];
const STROKE = {
  neutral: '#CCD2DA',
  hot: '#C2543C',
  cool: '#0E6FA3'
};
const TEXT = {
  neutral: '#49515B',
  hot: '#b8492e',
  cool: '#0F5480'
};
function Row({
  steps,
  y,
  color
}: {
  steps: Step[];
  y: number;
  color: 'hot' | 'cool';
}) {
  const stroke = color === 'hot' ? '#C2543C' : '#0E6FA3';
  const centers = steps.map(s => s.x + s.w / 2);
  return <>
      {steps.map(s => <g key={s.label} opacity={s.faded ? 0.4 : 1} data-id="element-173" data-mp="1t9jdvousj96x">
          <rect x={s.x} y={y} width={s.w} height={26} fill="#fff" stroke={STROKE[s.tone]} strokeDasharray={s.faded ? '3 3' : undefined} data-id="element-174" data-mp="1tb48tfl0ou5b" />
          <text x={s.x + s.w / 2} y={y + 17} textAnchor="middle" fill={TEXT[s.tone]} data-id="element-175" data-mp="1fhv1rlkjkkpy">
            {s.label}
          </text>
        </g>)}
      {centers.slice(0, -1).map((from, i) => {
      const to = centers[i + 1];
      const mid = (from + to) / 2;
      return <g key={`arc-${i}`} opacity={steps[i + 1].faded ? 0.4 : 1} data-id="element-176" data-mp="15ls00gtlqo3j" data-mpi={i}>
            <path d={`M${from} ${y} Q ${mid} ${y - 22} ${to} ${y}`} stroke={stroke} strokeWidth="1.3" strokeDasharray="2.5 3.5" fill="none" data-id="element-177" data-mp="11i3us4sg04lg" />
            <polygon points={`${to},${y} ${to - 4.5},${y - 6.5} ${to + 1.5},${y - 6}`} fill={stroke} data-id="element-178" data-mp="2vtikillge417" />
          </g>;
    })}
    </>;
}

/** Revision cycles before approval: ungoverned drafting versus musts seated first. */
export function ReworkChart() {
  return <div style={{
    width: '100%'
  }} data-id="element-179" data-mp="3etepjioqtc49">
      <svg viewBox="0 0 600 396" width="100%" fill="none" fontFamily="JetBrains Mono, monospace" fontSize="10.5" role="img" aria-label="Revisions before approval: ungoverned drafting averages five revision cycles with escalating cost; with musts seated first, fewer than two." data-id="element-180" data-mp="310ozn363fn1v">
        <text x="0" y="16" fill="#151A21" fontWeight="600" fontSize="12" letterSpacing="1.2" data-id="element-181" data-mp="1kabjtbhow7y">
          REVISIONS BEFORE APPROVAL
        </text>
        <line x1="0" y1="48" x2="600" y2="48" stroke="#E7EBEF" data-id="element-182" data-mp="3jtkoljxce0wd" />

        <text x="0" y="84" fill="#b8492e" fontSize="12" letterSpacing="1" data-id="element-183" data-mp="3ooq6m7nmivf6">
          UNGOVERNED
        </text>
        <Row steps={UNGOVERNED} y={124} color="hot" data-id="element-184" data-mp="28aq8n28c9fuk" />
        <rect x="388" y="124" width="36" height="26" fill="#151A21" data-id="element-185" data-mp="2dbe90eigj91g" />
        <text x="406" y="141" textAnchor="middle" fill="#fff" data-id="element-186" data-mp="25rlhb3ge2jxu">
          &#10003;
        </text>
        <g data-id="element-187" data-mp="20sin6e1y5y4n">
          <path d="M349 124 Q 377.5 102 406 124" stroke="#C2543C" strokeWidth="1.3" strokeDasharray="2.5 3.5" fill="none" data-id="element-188" data-mp="20sscdzf6419a" />
          <polygon points="406,124 401.5,117.5 407.5,118" fill="#C2543C" data-id="element-189" data-mp="2er8jvskutvsh" />
        </g>
        {['$', '$$', '$$$', '$$$$'].map((m, i) => <text key={m} x={163 + i * 62} y="168" textAnchor="middle" fill="#C2543C" fontSize="12" fontWeight="600" data-id="element-190" data-mp="29r41ty4ast05" data-mpi={i}>
            {m}
          </text>)}
        <text x="600" y="160" textAnchor="end" fill="#C2543C" fontFamily="JetBrains Mono, monospace" fontWeight="700" fontSize="48" data-id="element-191" data-mp="1vtp5e9fmnkyo">
          5
        </text>

        <line x1="0" y1="198" x2="600" y2="198" stroke="#E7EBEF" data-id="element-192" data-mp="13bblldr4lzil" />

        <text x="0" y="226" fill="#0F5480" fontSize="12" letterSpacing="1" data-id="element-193" data-mp="yd0bd1wx5mzu">
          MUSTS SEATED FIRST
        </text>
        <Row steps={SEATED} y={266} color="cool" data-id="element-194" data-mp="1c2br0ij5205z" />
        <rect x="202" y="266" width="36" height="26" fill="#151A21" data-id="element-195" data-mp="1gzll4bhh3005" />
        <text x="220" y="283" textAnchor="middle" fill="#fff" data-id="element-196" data-mp="1gxinp6dzq4le">
          &#10003;
        </text>
        <g data-id="element-197" data-mp="1lwvchwof9lqm">
          <path d="M163 266 Q 191.5 244 220 266" stroke="#0E6FA3" strokeWidth="1.3" strokeDasharray="2.5 3.5" fill="none" data-id="element-198" data-mp="1qpodc9grs9xf" />
          <polygon points="220,266 215.5,259.5 221.5,260" fill="#0E6FA3" data-id="element-199" data-mp="1urrsp96k7ynz" />
        </g>
        <text x="163" y="310" textAnchor="middle" fill="#0E6FA3" fontSize="12" fontWeight="600" data-id="element-200" data-mp="1zq32xl0rob6q">
          $
        </text>
        <text x="600" y="302" textAnchor="end" fill="#0E6FA3" fontFamily="JetBrains Mono, monospace" fontWeight="700" fontSize="48" data-id="element-201" data-mp="24p64gvhjfz9y">
          &#60;2
        </text>

        <line x1="0" y1="332" x2="600" y2="332" stroke="#E7EBEF" data-id="element-202" data-mp="adhrgyuva6s7" />
        <text x="0" y="358" fill="#151A21" fontSize="10.5" letterSpacing=".5" data-id="element-203" data-mp="fbatrgt1ugn7">
          AVG REVISION CYCLES PER CONSEQUENTIAL DRAFT &#183; r = revision
        </text>
        <text x="0" y="388" fill="#0E6FA3" fontSize="16" fontWeight="600" data-id="element-204" data-mp="2ntj5hgi77x42">
          The rework loop, reduced by must-haves.
        </text>
      </svg>
    </div>;
}